import React, { useState } from 'react';
import { AlertCircle, Loader2, Lock, Mail, MessageSquare } from 'lucide-react';
import { useCalendar } from '../calendar/useCalendar';
import { useOutlook } from '../calendar/useOutlook';
import { useSummaryEmail } from '../email/useSummaryEmail';
import { useAsyncData } from '../directory/useAsyncData';
import { Button } from '../../components/ui/Button';
import { Label } from '../../components/ui/Input';
import { Switch } from '../../components/ui/Switch';
import { GoogleMark, MicrosoftMark } from '../../components/ui/brandIcons';
import type { ToastKind } from '../../components/Toast';
import { useLanguage } from '../../lib/i18n';
import { IS_WEB } from '../../lib/platform';
import { Badge, ConnectedRow, DetailRow, DiscoverCard, SetupNote, type IntegrationEntry } from './IntegrationTiles';
import { IntegrationDetailDialog } from './IntegrationDetailDialog';

interface IntegrationsPanelProps {
  onNotify: (kind: ToastKind, text: string) => void;
}

/**
 * Entegrasyonlar — yalnızca kullanıcının kendi hesabıyla bağladığı uygulamalar:
 * takvimler ve özet e-postası. Deepgram/Claude/Skribby/S3 gibi anahtarı .env'den gelen
 * servisler buraya girmiyor; onların durumu Yönetim > Yapılandırma
 * Tanılaması'nda.
 *
 * Bağlı olanlar üstte liste, geri kalanlar altta "Keşfet" ızgarası. Ana
 * ekrandaki "Entegrasyonlar" ile Ayarlar > Entegrasyonlar aynı panel.
 */
export const IntegrationsPanel: React.FC<IntegrationsPanelProps> = ({ onNotify }) => {
  const { t } = useLanguage();
  const [openId, setOpenId] = useState<string | null>(null);

  // — Takvimler —
  const calendar = useCalendar();
  // 'error': hesap bağlı ama etkinlikler okunamadı (ağ) — "bağla" önerme.
  const isGoogleConnected =
    calendar.status === 'ready' || calendar.status === 'loading' || calendar.status === 'error';
  const isGoogleExpired = calendar.status === 'auth_expired';
  const [isGoogleDisconnecting, setIsGoogleDisconnecting] = useState(false);
  const disconnectGoogle = async () => {
    setIsGoogleDisconnecting(true);
    try {
      await calendar.disconnect();
    } catch (err) {
      onNotify('error', (err as Error).message);
    } finally {
      setIsGoogleDisconnecting(false);
    }
  };

  const outlook = useOutlook(onNotify);
  const isOutlookBusy = outlook.isBusy;
  const connectOutlook = outlook.connect;
  const disconnectOutlook = outlook.disconnect;

  // — Otomatik bot katılımı —
  const [autoJoinRevision, setAutoJoinRevision] = useState(0);
  const autoJoin = useAsyncData(() => window.zabit.getAutoJoinPrefs(), autoJoinRevision);
  const toggleAutoJoin = async (enabled: boolean) => {
    try {
      await window.zabit.setAutoJoinEnabled(enabled);
      onNotify(
        'info',
        enabled
          ? t(
              'Bot, kabul ettiğin Meet/Zoom/Teams toplantılarına başlangıç saatinde otomatik katılacak.',
              'The bot will join the Meet/Zoom/Teams meetings you accepted at their start time.'
            )
          : t('Otomatik bot katılımı kapatıldı.', 'Automatic bot joining is off.')
      );
    } catch (err) {
      onNotify('error', (err as Error).message);
    } finally {
      setAutoJoinRevision((value) => value + 1);
    }
  };

  // — Özet e-postası —
  const email = useSummaryEmail(true);
  /** Google izinlerini yeniden iste (takvim). */
  const refreshGoogleAccess = () => void calendar.connect().then(() => email.reload());

  const entries: IntegrationEntry[] = [
    {
      id: 'google-calendar',
      name: t('Google Takvim', 'Google Calendar'),
      icon: <GoogleMark />,
      description: t(
        'Toplantıların, katılımcılar ve Meet bağlantıları buradan okunuyor.',
        'Your meetings, their participants and Meet links are read from here.'
      ),
      connected: isGoogleConnected,
      detail: calendar.profile?.email ?? t('Bağlı', 'Connected'),
      badge: (
        <Badge tone="brand">
          <Lock className="size-2.5" />
          {t('Zorunlu', 'Required')}
        </Badge>
      ),
      action: isGoogleConnected
        ? undefined
        : {
            label: isGoogleExpired ? t('Yeniden bağla', 'Reconnect') : t('Bağla', 'Connect'),
            variant: 'default',
            busy: calendar.status === 'connecting',
            disabled: calendar.status === 'connecting',
            onClick: () => void calendar.connect(),
          },
      body: (
        <div className="space-y-4">
          <DetailRow
            title={t('Bağlı hesap', 'Connected account')}
            description={t(
              'Girişin bir parçası: TalkNote takvimini okuyamazsa toplantı listesi boş kalır.',
              'Part of signing in: without it TalkNote can’t read your calendar and the meeting list stays empty.'
            )}
          >
            {isGoogleConnected ? (
              <Badge tone="ok">{calendar.profile?.email ?? t('Bağlı', 'Connected')}</Badge>
            ) : isGoogleExpired ? (
              <Badge tone="warn">{t('Yetki süresi doldu', 'Access expired')}</Badge>
            ) : (
              <Badge>{t('Bağlı değil', 'Not connected')}</Badge>
            )}
          </DetailRow>

          <DetailRow
            title={t('Toplantılara otomatik bot gönder', 'Send the bot to meetings automatically')}
            description={t(
              'Bot, takvimindeki Meet/Zoom/Teams toplantılarına başlangıç saatinde katılır (reddettiklerin hariç). Tek bir toplantıyı kapatmak ya da açmak için ana sayfadaki toplantının bot düğmesini kullan.',
              'The bot joins the Meet/Zoom/Teams meetings on your calendar at their start time (except ones you declined). To switch a single meeting off or on, use its bot switch on the home page.'
            )}
          >
            {autoJoin.data ? (
              <Switch
                checked={autoJoin.data.autoJoinBot}
                onChange={(next) => void toggleAutoJoin(next)}
                label={t('Otomatik bot katılımı', 'Automatic bot joining')}
              />
            ) : (
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            )}
          </DetailRow>

          {calendar.connectError && <p className="text-[0.8rem] font-medium text-destructive">{calendar.connectError}</p>}
        </div>
      ),
      footer: isGoogleConnected ? (
        <>
          <Button variant="outline" disabled={isGoogleDisconnecting} onClick={() => void disconnectGoogle()}>
            {isGoogleDisconnecting && <Loader2 className="size-4 animate-spin" />}
            {t('Bağlantıyı kes', 'Disconnect')}
          </Button>
          <Button variant="outline" disabled={calendar.status === 'connecting' || isGoogleDisconnecting} onClick={refreshGoogleAccess}>
            {calendar.status === 'connecting' && <Loader2 className="size-4 animate-spin" />}
            {t('Yetkileri yenile', 'Refresh permissions')}
          </Button>
        </>
      ) : undefined,
    },

    {
      id: 'outlook',
      name: t('Outlook Takvimi', 'Outlook Calendar'),
      icon: <MicrosoftMark />,
      description: t(
        'Microsoft 365 / Outlook.com takvimindeki toplantılar Google takvimiyle birleşir.',
        'Meetings from your Microsoft 365 / Outlook.com calendar are merged with Google Calendar.'
      ),
      connected: Boolean(outlook.data?.connected),
      detail: outlook.data?.profile?.email ?? t('Bağlı', 'Connected'),
      action: outlook.data?.connected
        ? undefined
        : {
            label: outlook.data?.expired ? t('Yeniden bağla', 'Reconnect') : t('Bağla', 'Connect'),
            variant: 'default',
            busy: isOutlookBusy,
            disabled: isOutlookBusy || !outlook.data?.configured,
            onClick: () => void connectOutlook(),
          },
      body: (
        <div className="space-y-4">
          <DetailRow
            title={t('Durum', 'Status')}
            description={t(
              'Teams, Zoom ve Meet bağlantıları iki takvimden de okunur; bot ikisine de katılabilir.',
              'Teams, Zoom and Meet links are read from both calendars, and the bot can join either.'
            )}
          >
            {!outlook.data ? (
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            ) : !outlook.data.configured ? (
              <Badge>{t('Yapılandırılmadı', 'Not configured')}</Badge>
            ) : outlook.data.connected ? (
              <Badge tone="ok">{outlook.data.profile?.email ?? t('Bağlı', 'Connected')}</Badge>
            ) : outlook.data.expired ? (
              <Badge tone="warn">{t('Yetki süresi doldu', 'Access expired')}</Badge>
            ) : (
              <Badge>{t('Bağlı değil', 'Not connected')}</Badge>
            )}
          </DetailRow>

          {outlook.data && !outlook.data.configured && (
            <SetupNote>
              {IS_WEB
                ? t(
                    'Microsoft bağlantısı sunucuda açık değil; bağlanabilmesi için uygulamanın Azure kaydı gerekiyor.',
                    'The Microsoft connection isn’t enabled on the server; connecting needs the app’s Azure registration.'
                  )
                : t(
                    'Bu bilgisayarda Microsoft bağlantısı açık değil; bağlanabilmesi için uygulamanın Azure kaydı gerekiyor.',
                    'The Microsoft connection isn’t enabled on this computer; connecting needs the app’s Azure registration.'
                  )}
            </SetupNote>
          )}
        </div>
      ),
      footer: outlook.data?.connected ? (
        <Button variant="outline" disabled={isOutlookBusy} onClick={() => void disconnectOutlook()}>
          {isOutlookBusy && <Loader2 className="size-4 animate-spin" />}
          {t('Bağlantıyı kes', 'Disconnect')}
        </Button>
      ) : undefined,
    },

    {
      id: 'summary-email',
      name: t('Özet e-postası', 'Summary email'),
      icon: <Mail className="text-brand" />,
      description: t(
        'Özet hazır olduğunda TalkNote’un adresinden seçtiğin adrese gönderilir.',
        'When a summary is ready, TalkNote emails it to an address you choose.'
      ),
      connected: email.status.canSend,
      detail: email.status.enabled
        ? email.status.recipient || t('Hesabının adresine', 'To your account address')
        : t('Kapalı', 'Off'),
      body: (
        <div className="space-y-4">
          <DetailRow
            title={t('Otomatik gönder', 'Send automatically')}
            description={t(
              'Kayıt bitip özet üretildiğinde özet, kararlar ve aksiyon maddeleri gönderilir. Ses kaydı gönderilmez.',
              'When a recording ends and the summary is ready, the summary, decisions and action items are sent. The audio is not.'
            )}
          >
            {email.isLoading ? (
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            ) : (
              <Switch
                checked={email.status.enabled}
                onChange={(next) => void email.savePrefs({ summaryEmailEnabled: next })}
                label={t('Otomatik özet e-postası', 'Automatic summary email')}
              />
            )}
          </DetailRow>

          <div className="grid gap-2 border-t border-border pt-4">
            <Label>{t('Gönderilecek adres', 'Send to')}</Label>
            <p className="text-sm">{email.status.recipient || t('Hesap adresin', 'Your account address')}</p>
            <p className="text-[0.8rem] text-muted-foreground">
              {t(
                'Özet her zaman giriş yaptığın hesabın adresine gider.',
                'The summary always goes to the address of the account you signed in with.'
              )}
            </p>
          </div>

        </div>
      ),
    },

    {
      id: 'slack',
      name: 'Slack',
      icon: <MessageSquare className="text-violet-500" />,
      description: t('Toplantı özetini seçtiğin kanala gönder.', 'Send meeting summaries to a channel you choose.'),
      connected: false,
      action: { label: t('Yakında', 'Soon'), disabled: true, onClick: () => undefined },
      body: (
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t(
            'Slack bağlantısı henüz hazır değil. Hazır olduğunda özet, kararlar ve aksiyon maddeleri seçtiğin kanala kayıt biter bitmez düşecek.',
            'The Slack connection isn’t ready yet. When it is, the summary, decisions and action items will land in the channel you choose as soon as a recording ends.'
          )}
        </p>
      ),
    },
  ];

  // Durumlar gelmeden kartlar "bağlı değil" görünüp sonra yer değiştirmesin.
  const isPlacementReady = Boolean(outlook.data) && !email.isLoading;
  const connected = entries.filter((entry) => entry.connected);
  const discover = entries.filter((entry) => !entry.connected);
  const openEntry = entries.find((entry) => entry.id === openId) ?? null;

  return (
    <div className="@container space-y-8">
      <section>
        <h2 className="px-3 text-lg font-semibold tracking-tight text-foreground">
          {t('Entegrasyonlarım', 'My integrations')}
        </h2>
        <div className="mt-2">
          {!isPlacementReady ? (
            <div className="space-y-1">
              {[0, 1].map((row) => (
                <div key={row} className="h-16 animate-pulse rounded-xl bg-muted/60" />
              ))}
            </div>
          ) : connected.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              {t(
                'Henüz bağlı bir uygulama yok. Aşağıdan başlayabilirsin.',
                'Nothing connected yet. Start from the list below.'
              )}
            </p>
          ) : (
            connected.map((entry) => <ConnectedRow key={entry.id} entry={entry} onOpen={() => setOpenId(entry.id)} />)
          )}
        </div>
      </section>

      <section>
        <h2 className="px-3 text-lg font-semibold tracking-tight text-foreground">{t('Keşfet', 'Discover')}</h2>
        <div className="mt-3 grid gap-4 @xl:grid-cols-2 @4xl:grid-cols-3">
          {isPlacementReady
            ? discover.map((entry) => <DiscoverCard key={entry.id} entry={entry} onOpen={() => setOpenId(entry.id)} />)
            : [0, 1].map((card) => <div key={card} className="h-40 animate-pulse rounded-xl bg-muted/60" />)}
        </div>
      </section>

      <IntegrationDetailDialog entry={openEntry} onClose={() => setOpenId(null)} />
    </div>
  );
};
